import { useState } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const CountrySelector = ({ variant = 'default' }) => {
  const { country, setCountry, countryInfo, currencyInfo, COUNTRIES } = useCurrency();
  const [open, setOpen] = useState(false);

  // Group countries by region
  const regions = {};
  Object.entries(COUNTRIES).forEach(([code, info]) => {
    if (!regions[info.region]) {
      regions[info.region] = [];
    }
    regions[info.region].push({ code, ...info });
  });

  const handleSelect = (code) => {
    setCountry(code);
    setOpen(false);
  };

  if (variant === 'compact') {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1.5 h-8">
            <span className="text-lg">{countryInfo.flag}</span>
            <span className="text-xs font-medium">{currencyInfo.symbol}</span>
            <ChevronDown className="w-3 h-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {Object.entries(regions).map(([region, countries]) => (
            <div key={region}>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {region}
              </DropdownMenuLabel>
              {countries.map((c) => (
                <DropdownMenuItem
                  key={c.code}
                  onClick={() => handleSelect(c.code)}
                  className={c.code === country ? 'bg-accent' : ''}
                >
                  <span className="mr-2">{c.flag}</span>
                  <span className="flex-1">{c.name}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Globe className="w-4 h-4" />
          <span className="text-lg">{countryInfo.flag}</span>
          <span>{countryInfo.name}</span>
          <span className="text-muted-foreground">({currencyInfo.symbol})</span>
          <ChevronDown className="w-4 h-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Select Your Country
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {Object.entries(regions).map(([region, countries]) => (
          <div key={region}>
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              {region}
            </DropdownMenuLabel>
            {countries.map((c) => (
              <DropdownMenuItem
                key={c.code}
                onClick={() => handleSelect(c.code)}
                className={`cursor-pointer ${c.code === country ? 'bg-accent' : ''}`}
              >
                <span className="mr-2 text-lg">{c.flag}</span>
                <span className="flex-1">{c.name}</span>
                {c.code === country && (
                  <span className="text-xs text-green-600">✓</span>
                )}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CountrySelector;
